import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../assets/cograd-blue-logo.webp";
import "./AdminDashboard.scss";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

const TABS = [
  { key: "contacts",    label: "Contact Us",       endpoint: "/api/admin/contacts"    },
  { key: "careers",     label: "Careers",           endpoint: "/api/admin/careers"     },
  { key: "incubations", label: "Incubations",       endpoint: "/api/admin/incubations" },
  { key: "ncf",         label: "NCF",               endpoint: "/api/admin/ncf"         },
  { key: "fellowship",  label: "Fellowship",        endpoint: "/api/admin/fellowship"  },
  { key: "nextgen",     label: "NextGen",           endpoint: "/api/admin/nextgen"     },
  // status=all so an abandoned or failed checkout stays visible, not just paid seats.
  { key: "hackathon",   label: "Hackathon",         endpoint: "/api/admin/hackathon?status=all" },
];

/**
 * A hackathon row carries a nested members array and an amount in paise, which
 * the generic table cannot render. Flatten it into the shape every other tab
 * uses so the table, search, CSV export and mobile cards all keep working.
 */
const flattenHackathon = (r) => {
  const lead = (r.members || [])[0] || {};
  return {
    ...r,
    name:        r.mode === "team" ? r.teamName : lead.name || "",
    lead:        lead.name || "",
    leadEmail:   lead.email || "",
    leadPhone:   lead.phone || "",
    memberNames: (r.members || []).map((m) => m.name + " (Cl " + m.studentClass + ")").join(", "),
    amount:      typeof r.amountPaise === "number" ? r.amountPaise / 100 : null,
  };
};

const COL_DEFS = {
  contacts:    ["name","email","phone","intent","how","message","createdAt"],
  careers:     ["name","email","phone","position","experience","portfolio","resumeLink","message","createdAt"],
  incubations: ["schoolName","location","board","totalStudents","totalClassrooms","affiliationValidity","contactName","contactNo","designation","createdAt"],
  ncf:         ["institutionName","contactName","email","phone","city","institutionType","teacherCount","trainingMode","message","createdAt"],
  fellowship:  ["name","email","phone","education","experience","interests","source","additional","createdAt"],
  nextgen:     ["schoolName","contactName","email","phone","city","schoolType","studentCount","hasAtlLab","message","createdAt"],
  hackathon:   ["status","name","mode","students","amount","lead","leadEmail","leadPhone","memberNames","school","city","state","board","mentorName","mentorPhone","guardianName","guardianPhone","paymentId","createdAt"],
};

const CARD_PRIMARY = {
  contacts:    ["name","email","phone","intent"],
  careers:     ["name","email","phone","position"],
  incubations: ["schoolName","contactName","contactNo","location"],
  ncf:         ["institutionName","contactName","email","phone"],
  fellowship:  ["name","email","phone","education"],
  nextgen:     ["schoolName","contactName","email","phone"],
  hackathon:   ["status","amount","leadEmail","leadPhone"],
};

const LABEL = {
  name: "Name", email: "Email", phone: "Phone", intent: "Intent",
  how: "Source", message: "Message", createdAt: "Date",
  position: "Position", experience: "Experience", portfolio: "Portfolio",
  resumeLink: "Resume",
  schoolName: "School", location: "Location", board: "Board",
  totalStudents: "Students", totalClassrooms: "Classrooms",
  affiliationValidity: "Affiliation", contactName: "Contact Person",
  contactNo: "Contact No.", designation: "Designation",
  institutionName: "Institution", institutionType: "Type",
  teacherCount: "Teachers", trainingMode: "Mode", city: "City",
  education: "Education",
  interests: "Interests", source: "Source", additional: "Additional",
  schoolType: "School Type", studentCount: "No. of Students", hasAtlLab: "ATL Lab",
  status: "Status", mode: "Type", students: "Students", amount: "Amount",
  lead: "Lead Student", leadEmail: "Lead Email", leadPhone: "Lead Phone",
  memberNames: "Members", teamName: "Team", paymentId: "Payment ID",
  mentorName: "Mentor", mentorPhone: "Mentor Phone",
  guardianName: "Guardian", guardianPhone: "Guardian Phone", state: "State",
};

function fmtVal(val, key) {
  if (!val) return "—";
  if (key === "createdAt") return new Date(val).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" });
  if (key === "amount") return "\u20B9" + Number(val).toLocaleString("en-IN");
  if (key === "status") return val === "created" ? "unpaid" : val;
  return val;
}

function fmt(val, key) {
  if (!val) return <span className="adm-dash__empty">—</span>;
  if (key === "createdAt") return new Date(val).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" });
  if (key === "resumeLink" || key === "portfolio")
    return <a href={val} target="_blank" rel="noopener noreferrer" className="adm-dash__link">View ↗</a>;
  if (key === "message") return <span className="adm-dash__msg" title={val}>{val.length > 60 ? val.slice(0,60)+"…" : val}</span>;
  if (key === "amount") return <span className="adm-dash__amt">{"\u20B9" + Number(val).toLocaleString("en-IN")}</span>;
  if (key === "status")
    return <span className={`adm-dash__badge adm-dash__badge--${val}`}>{val === "created" ? "unpaid" : val}</span>;
  if (key === "paymentId") return <span className="adm-dash__mono">{val}</span>;
  return val;
}

export default function AdminDashboard() {
  const navigate  = useNavigate();
  const [tab,       setTab]       = useState("contacts");
  const [rows,      setRows]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [confirmId, setConfirmId] = useState(null);
  const [expandId,  setExpandId]  = useState(null);

  const token = localStorage.getItem("admin_token");

  useEffect(() => {
    if (!token) { navigate("/admin"); return; }
    setUserEmail(localStorage.getItem("admin_email") || "");
  }, [token, navigate]);

  const fetchData = useCallback(async (tabKey) => {
    const t = TABS.find(t => t.key === tabKey);
    setLoading(true);
    setSearch("");
    setExpandId(null);
    try {
      const res = await fetch(`${API_URL}${t.endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) { navigate("/admin"); return; }
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setRows(tabKey === "hackathon" ? list.map(flattenHackathon) : list);
    } catch {
      setRows([]);
    }
    setLoading(false);
  }, [token, navigate]);

  useEffect(() => {
    if (token) fetchData(tab);
  }, [tab, token, fetchData]);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_email");
    navigate("/admin");
  };

  const handleDelete = async (id) => {
    setConfirmId(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/${tab}/${id}`, {
        method:  "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) { navigate("/admin"); return; }
      if (res.ok) { setRows(prev => prev.filter(r => r._id !== id)); return; }
      const data = await res.json().catch(() => ({}));
      alert(data.error || "Delete failed. Please try again.");
    } catch {
      alert("Delete failed. Please try again.");
    }
  };

  const cols     = COL_DEFS[tab];
  const cardCols = CARD_PRIMARY[tab];
  const filtered = rows.filter(r =>
    Object.values(r).some(v => String(v || "").toLowerCase().includes(search.toLowerCase()))
  );

  const exportCSV = () => {
    const header = cols.map(c => LABEL[c] || c).join(",");
    const csvRows = filtered.map(row =>
      cols.map(c => {
        let val = row[c] ?? "";
        if (c === "createdAt" && val) val = new Date(val).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" });
        return `"${String(val).replace(/"/g, '""')}"`;
      }).join(",")
    );
    const blob = new Blob([[header, ...csvRows].join("\n")], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = `${tab}_${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const DeleteBtn = ({ id, row }) => row?.status === "paid" ? (
    <span className="adm-dash__locked" title="A paid registration cannot be deleted">Paid</span>
  ) : confirmId === id ? (
    <span className="adm-dash__confirm">
      <button className="adm-dash__confirm-yes" onClick={() => handleDelete(id)}>Yes, Delete</button>
      <button className="adm-dash__confirm-no"  onClick={() => setConfirmId(null)}>Cancel</button>
    </span>
  ) : (
    <button className="adm-dash__delete" onClick={() => setConfirmId(id)}>Delete</button>
  );

  return (
    <div className="adm-dash">

      {/* Sidebar */}
      <aside className="adm-dash__side">

        {/* Desktop logo */}
        <img src={logo} alt="CoGrad" className="adm-dash__side-logo adm-dash__desktop-only" />

        {/* Mobile header */}
        <div className="adm-dash__mob-header">
          <img src={logo} alt="CoGrad" className="adm-dash__side-logo" />
          <div className="adm-dash__mob-right">
            <span className="adm-dash__mob-email">{userEmail}</span>
            <button className="adm-dash__mob-logout" onClick={handleLogout}>Sign out</button>
          </div>
        </div>

        {/* Desktop label */}
        <p className="adm-dash__side-label">Admin Portal</p>

        <nav className="adm-dash__nav">
          {TABS.map(t => (
            <button key={t.key}
              className={`adm-dash__nav-item${tab === t.key ? " adm-dash__nav-item--on" : ""}`}
              onClick={() => setTab(t.key)}>
              {t.label}
              {tab === t.key && <span className="adm-dash__nav-dot" />}
            </button>
          ))}
        </nav>

        <div className="adm-dash__side-footer">
          <p className="adm-dash__side-email">{userEmail}</p>
          <button className="adm-dash__logout" onClick={handleLogout}>Sign out</button>
        </div>
      </aside>

      {/* Main */}
      <main className="adm-dash__main">
        <div className="adm-dash__header">
          <div>
            <h1 className="adm-dash__title">{TABS.find(t=>t.key===tab)?.label}</h1>
            <p className="adm-dash__count">{filtered.length} submission{filtered.length !== 1 ? "s" : ""}</p>
          </div>
          <div className="adm-dash__header-right">
            <input
              className="adm-dash__search"
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <button className="adm-dash__export" onClick={exportCSV} disabled={filtered.length === 0}>
              Export CSV
            </button>
          </div>
        </div>

        {loading ? (
          <div className="adm-dash__loader">
            <div className="adm-dash__spinner" />
            <p>Loading...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="adm-dash__empty-state">
            <p>No submissions yet.</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="adm-dash__table-wrap adm-dash__desktop-only">
              <table className="adm-dash__table">
                <thead>
                  <tr>
                    {cols.map(c => <th key={c}>{LABEL[c] || c}</th>)}
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(row => (
                    <tr key={row._id}>
                      {cols.map(c => <td key={c}>{fmt(row[c], c)}</td>)}
                      <td><DeleteBtn id={row._id} row={row} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="adm-dash__cards adm-dash__mobile-only">
              {filtered.map(row => (
                <div key={row._id} className="adm-dash__card">
                  <div className="adm-dash__card-head">
                    <div>
                      <p className="adm-dash__card-name">{row.name || row.schoolName || row.institutionName || "—"}</p>
                      <p className="adm-dash__card-date">{fmtVal(row.createdAt, "createdAt")}</p>
                    </div>
                    <DeleteBtn id={row._id} row={row} />
                  </div>
                  <div className="adm-dash__card-grid">
                    {cardCols.filter(c => c !== "name" && c !== "schoolName" && c !== "institutionName").map(c => (
                      <div key={c} className="adm-dash__card-field">
                        <span className="adm-dash__card-label">{LABEL[c] || c}</span>
                        <span className="adm-dash__card-value">{fmtVal(row[c], c) || "—"}</span>
                      </div>
                    ))}
                  </div>
                  {(row.message || row.interests || row.additional) && (
                    <div className="adm-dash__card-msg">
                      <span className="adm-dash__card-label">Message</span>
                      <p>{row.message || row.interests || row.additional}</p>
                    </div>
                  )}
                  <button
                    className="adm-dash__card-more"
                    onClick={() => setExpandId(expandId === row._id ? null : row._id)}
                  >
                    {expandId === row._id ? "Show less ▲" : "Show all details ▼"}
                  </button>
                  {expandId === row._id && (
                    <div className="adm-dash__card-expand">
                      {cols.filter(c => !cardCols.includes(c) && c !== "createdAt" && c !== "message" && c !== "interests" && c !== "additional").map(c => (
                        <div key={c} className="adm-dash__card-field">
                          <span className="adm-dash__card-label">{LABEL[c] || c}</span>
                          <span className="adm-dash__card-value">{fmtVal(row[c], c) || "—"}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
